import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMenu } from '../../contexts/MenuContext';
import { useLocation } from '../../contexts/LocationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiPlus, FiTrash2, FiSave } = FiIcons;

const MenuItemForm = ({ item, onClose }) => {
  const { addMenuItem, updateMenuItem, menuCategories } = useMenu();
  const { locations } = useLocation();
  
  const [formData, setFormData] = useState({
    name: item?.name || '',
    category: item?.category || item?.category_id || menuCategories[0]?.id || '',
    price: item?.price || 0,
    image: item?.image || '',
    description: item?.description || '',
    ingredients: item?.ingredients || [],
    customizable: item?.customizable || false,
    options: item?.options || {
      bread: [],
      protein: [],
      toppings: [],
      extras: []
    },
    locationIds: item?.location_ids || item?.locationIds || []
  });
  
  const [newIngredient, setNewIngredient] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => ({
      ...prev,
      locationIds: prev.locationIds.includes(locationId)
        ? prev.locationIds.filter(id => id !== locationId)
        : [...prev.locationIds, locationId]
    }));
  };

  const handleOptionsChange = (category, index, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: {
        ...prev.options,
        [category]: prev.options[category].map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const addOption = (category, name = '', price = 0) => {
    if (name.trim()) {
      setFormData(prev => ({
        ...prev,
        options: {
          ...prev.options,
          [category]: [...prev.options[category], { name: name.trim(), price: parseFloat(price) || 0 }]
        }
      }));
    }
  };

  const removeOption = (category, index) => {
    setFormData(prev => ({
      ...prev,
      options: {
        ...prev.options,
        [category]: prev.options[category].filter((_, i) => i !== index)
      }
    }));
  };

  const addIngredient = () => {
    if (newIngredient.trim()) {
      setFormData(prev => ({
        ...prev,
        ingredients: [...prev.ingredients, newIngredient.trim()]
      }));
      setNewIngredient('');
    }
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.category || !formData.price) {
      setError('Please fill in all required fields');
      return;
    }

    const result = item 
      ? await updateMenuItem(item.id, formData)
      : await addMenuItem(formData);
      
    if (result.success) {
      onClose();
    } else {
      setError(result.error);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-warm-brown/10 p-6 flex items-center justify-between">
            <h2 className="text-2xl font-playfair text-warm-brown">
              {item ? 'Edit Menu Item' : 'Add New Menu Item'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-cream rounded-full transition-colors"
            >
              <SafeIcon icon={FiX} className="text-warm-brown text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
              >
                {error}
              </motion.div>
            )}

            {/* Basic Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-warm-brown font-medium mb-2">Item Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
              <div>
                <label className="block text-warm-brown font-medium mb-2">Category *</label>
                <select
                  required
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                >
                  <option value="">Select Category</option>
                  {menuCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.icon} {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-warm-brown font-medium mb-2">Price *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', parseFloat(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
              <div>
                <label className="block text-warm-brown font-medium mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => handleInputChange('image', e.target.value)}
                  className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                />
              </div>
            </div>

            <div>
              <label className="block text-warm-brown font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
              />
            </div>

            {/* Location Assignment */}
            <div>
              <label className="block text-warm-brown font-medium mb-2">Available at Locations</label>
              <div className="text-sm text-warm-brown/60 mb-3">
                Leave empty to make available at all locations
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {locations.map(location => (
                  <label key={location.id} className="flex items-center gap-3 p-3 bg-cream rounded-lg cursor-pointer hover:bg-warm-brown/5">
                    <input
                      type="checkbox"
                      checked={formData.locationIds.includes(location.id)}
                      onChange={() => handleLocationToggle(location.id)}
                      className="w-4 h-4 text-warm-brown border-warm-brown/20 rounded focus:ring-warm-brown/50"
                    />
                    <div>
                      <div className="font-medium text-warm-brown">{location.name}</div>
                      <div className="text-xs text-warm-brown/70">{location.address}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <label className="block text-warm-brown font-medium mb-2">Ingredients</label>
              <div className="space-y-2">
                {formData.ingredients.map((ingredient, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={ingredient}
                      onChange={(e) => {
                        const newIngredients = [...formData.ingredients];
                        newIngredients[index] = e.target.value;
                        handleInputChange('ingredients', newIngredients);
                      }}
                      className="flex-1 px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                    />
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="p-2 text-red-500 hover:text-red-700 transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="text-sm" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newIngredient}
                    onChange={(e) => setNewIngredient(e.target.value)}
                    placeholder="Add new ingredient"
                    className="flex-1 px-4 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
                  />
                  <button
                    type="button"
                    onClick={addIngredient}
                    className="p-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
                  >
                    <SafeIcon icon={FiPlus} className="text-sm" />
                  </button>
                </div>
              </div>
            </div>

            {/* Customizable Options */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <input
                  type="checkbox"
                  id="customizable"
                  checked={formData.customizable}
                  onChange={(e) => handleInputChange('customizable', e.target.checked)}
                  className="w-4 h-4 text-warm-brown border-warm-brown/20 rounded focus:ring-warm-brown/50"
                />
                <label htmlFor="customizable" className="text-warm-brown font-medium">
                  Allow customization
                </label>
              </div>

              {formData.customizable && (
                <div className="space-y-4 bg-cream rounded-lg p-4">
                  {['bread', 'protein', 'toppings', 'extras'].map(category => (
                    <CustomizationSection
                      key={category}
                      title={category.charAt(0).toUpperCase() + category.slice(1)}
                      items={formData.options[category]}
                      onAdd={(name, price) => addOption(category, name, price)}
                      onRemove={(index) => removeOption(category, index)}
                      onChange={(index, field, value) => handleOptionsChange(category, index, field, value)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-warm-brown text-cream py-3 rounded-lg font-medium hover:bg-warm-brown/90 transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiSave} className="text-sm" />
                {item ? 'Update Item' : 'Add Item'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 border border-warm-brown/20 text-warm-brown py-3 rounded-lg font-medium hover:bg-warm-brown/5 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Helper component for customization sections
const CustomizationSection = ({ title, items, onAdd, onRemove, onChange }) => {
  const [newName, setNewName] = useState('');
  const [newPrice, setNewPrice] = useState(0);

  const handleAdd = () => {
    if (newName.trim()) {
      onAdd(newName.trim(), newPrice);
      setNewName('');
      setNewPrice(0);
    }
  };

  return (
    <div>
      <label className="block text-warm-brown font-medium mb-2">{title} Options</label>
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={typeof item === 'string' ? item : item.name}
              onChange={(e) => onChange(index, 'name', e.target.value)}
              placeholder="Option name"
              className="flex-1 px-3 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
            />
            <input
              type="number"
              step="0.01"
              min="0"
              value={typeof item === 'string' ? 0 : item.price || 0}
              onChange={(e) => onChange(index, 'price', parseFloat(e.target.value) || 0)}
              placeholder="Price"
              className="w-24 px-3 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
            />
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="p-2 text-red-500 hover:text-red-700 transition-colors"
            >
              <SafeIcon icon={FiTrash2} className="text-sm" />
            </button>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`Add ${title.toLowerCase()} option`}
            className="flex-1 px-3 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
          <input
            type="number"
            step="0.01"
            min="0"
            value={newPrice}
            onChange={(e) => setNewPrice(parseFloat(e.target.value) || 0)}
            placeholder="Price"
            className="w-24 px-3 py-2 border border-warm-brown/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-warm-brown/50"
          />
          <button
            type="button"
            onClick={handleAdd}
            className="p-2 bg-warm-brown text-cream rounded-lg hover:bg-warm-brown/90 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default MenuItemForm;